import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { TASK_LABELS } from "./toefl-mock1/listeningData";
import * as mock1Data from "./toefl-mock1/listeningData";
import * as mock2Data from "./toefl-mock2/listeningData";
import * as mock3Data from "./toefl-mock3/listeningData";
import * as mock4Data from "./toefl-mock4/listeningData";
import * as mock5Data from "./toefl-mock5/listeningData";
import * as mock6Data from "./toefl-mock6/listeningData";
import * as mock7Data from "./toefl-mock7/listeningData";
import * as mock8Data from "./toefl-mock8/listeningData";
import * as mock9Data from "./toefl-mock9/listeningData";

const LISTENING_DATA = { 1: mock1Data, 2: mock2Data, 3: mock3Data, 4: mock4Data, 5: mock5Data, 6: mock6Data, 7: mock7Data, 8: mock8Data, 9: mock9Data };
import {
  C,
  TimerBadge,
  SubmittingScreen,
  useCountdown,
  usePageTop,
  useWindowWidth,
  useVoicesReady,
  scrollToTop,
  speakLines,
  speechSupported,
  estimateSpeechSeconds,
  routeForModule1,
  adaptiveBand,
  bandSummary,
  saveToeflResult,
} from "./toeflShared";

// ============================================================
// TOEFL iBT LISTENING — MOCK 1 (2026 format, multistage adaptive)
// Each recording plays once; you cannot go back to earlier items.
// ============================================================

const TASK_INSTRUCTIONS = {
  response: "Listen to the speaker, then choose the best response.",
  conversation: "Listen to the conversation, then answer the questions.",
  announcement: "Listen to the announcement, then answer the questions.",
  talk: "Listen to part of a talk in an academic course, then answer the questions.",
};

const answerKey = (itemId, q) => `${itemId}:${q}`;

function scoreItems(items, answers, startNumber) {
  let correct = 0;
  let total = 0;
  const breakdown = [];
  items.forEach((item) => {
    item.questions.forEach((q, qi) => {
      total += 1;
      const choice = answers[answerKey(item.id, qi)];
      const isCorrect = choice === q.correct;
      if (isCorrect) correct += 1;
      breakdown.push({
        number: startNumber + breakdown.length,
        category: TASK_LABELS[item.type],
        questionText:
          item.type === "response"
            ? `${item.lines[0].speaker}: "${item.lines[0].text}" — choose the best response.`
            : `${item.title}: ${q.question}`,
        userAnswerText: choice === undefined ? "No answer" : q.options[choice],
        correctAnswerText: q.options[q.correct],
        isCorrect,
      });
    });
  });
  return { correct, total, breakdown };
}

const countQuestions = (items) => items.reduce((n, it) => n + it.questions.length, 0);

function ToeflListening({ mock = 1 }) {
  const { listeningModules, MODULE_SECONDS } = LISTENING_DATA[mock] || mock1Data;
  const navigate = useNavigate();
  const width = useWindowWidth();
  const isMobile = width < 768;
  usePageTop();
  useVoicesReady();

  // stage: "module1" | "break" | "module2" | "submitting"
  const [stage, setStage] = useState("module1");
  const [path, setPath] = useState(null);
  const [itemIndex, setItemIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  // audio: "ready" | "playing" | "done"
  const [audio, setAudio] = useState("ready");
  const [showTranscript, setShowTranscript] = useState(false);
  const cancelSpeechRef = useRef(() => {});
  const fallbackTimerRef = useRef(null);
  const module1ScoreRef = useRef(null);
  const submittedRef = useRef(false);

  const items = stage === "module2" ? listeningModules[path] : listeningModules.module1;
  const item = items[itemIndex] || items[0];
  const inModule = stage === "module1" || stage === "module2";
  const ttsAvailable = speechSupported();

  const stopAudio = useCallback(() => {
    cancelSpeechRef.current();
    clearTimeout(fallbackTimerRef.current);
  }, []);

  useEffect(() => stopAudio, [stopAudio]);

  useEffect(() => {
    scrollToTop();
  }, [itemIndex, stage]);

  // ── Module transitions & submission ───────────────────────────
  const finishModule1 = useCallback(() => {
    stopAudio();
    const score = scoreItems(listeningModules.module1, answers, 1);
    module1ScoreRef.current = score;
    setPath(routeForModule1(score.correct, score.total));
    setStage("break");
  }, [answers, stopAudio, listeningModules]);

  const submitTest = useCallback(async () => {
    if (submittedRef.current) return;
    submittedRef.current = true;
    stopAudio();
    setStage("submitting");

    const m1 = module1ScoreRef.current || scoreItems(listeningModules.module1, answers, 1);
    // Exiting during Module 1 has no route yet — derive it from Module 1.
    const route = path || routeForModule1(m1.correct, m1.total);
    const m2 = scoreItems(listeningModules[route], answers, m1.total + 1);
    const correctCount = m1.correct + m2.correct;
    const total = m1.total + m2.total;
    const percentage = Math.round((correctCount / total) * 100);
    const summary = bandSummary(adaptiveBand(correctCount / total, route), route);
    const breakdown = [...m1.breakdown, ...m2.breakdown];

    const saveStatus = await saveToeflResult("TOEFL Listening Test", {
      format: "TOEFL iBT 2026",
      mock,
      rawScore: correctCount,
      total,
      percentage,
      module1: { correct: m1.correct, total: m1.total },
      module2: { correct: m2.correct, total: m2.total },
      ...summary,
      breakdown,
    });

    navigate("/quiz/toefl/result", {
      state: { correctCount, total, percentage, testType: "Listening", breakdown, ...summary, saveStatus },
    });
  }, [answers, path, navigate, stopAudio, mock, listeningModules]);

  const handleExpire = useCallback(() => {
    if (stage === "module1") finishModule1();
    else if (stage === "module2") submitTest();
  }, [stage, finishModule1, submitTest]);

  const [timeLeft, setTimeLeft] = useCountdown(MODULE_SECONDS.module1, inModule, handleExpire);

  // ── Audio ─────────────────────────────────────────────────────
  const play = () => {
    if (audio !== "ready") return;
    if (!ttsAvailable) {
      setShowTranscript(true);
      setAudio("done");
      return;
    }
    setAudio("playing");
    const finish = () => {
      clearTimeout(fallbackTimerRef.current);
      setAudio((a) => (a === "playing" ? "done" : a));
    };
    cancelSpeechRef.current = speakLines(item.lines, { onEnd: finish });
    // Some browsers never fire the final onend; don't leave the user stuck.
    fallbackTimerRef.current = setTimeout(finish, estimateSpeechSeconds(item.lines) * 1000);
  };

  // ── Navigation ────────────────────────────────────────────────
  const allAnswered = item.questions.every((_, qi) => answers[answerKey(item.id, qi)] !== undefined);
  const isLastItem = itemIndex === items.length - 1;

  const handleNext = () => {
    stopAudio();
    if (!isLastItem) {
      setItemIndex((i) => i + 1);
      setAudio("ready");
      setShowTranscript(false);
    } else if (stage === "module1") {
      finishModule1();
    } else {
      submitTest();
    }
  };

  // Exit ends the section early: unanswered questions count as wrong and
  // the result is saved, so the score still reaches the results page and profile.
  const handleExit = () => {
    if (!window.confirm("Exit the Listening test? Your answers so far will be scored and saved, and unanswered questions will count as incorrect.")) return;
    submitTest();
  };

  const startModule2 = () => {
    setItemIndex(0);
    setAudio("ready");
    setShowTranscript(false);
    setTimeLeft(MODULE_SECONDS.module2);
    setStage("module2");
  };

  // ==========================================================
  // RENDER
  // ==========================================================

  if (stage === "submitting") return <SubmittingScreen isMobile={isMobile} />;

  if (stage === "break") {
    return (
      <div style={{ ...containerStyle, maxWidth: "640px", padding: isMobile ? "100px 14px 40px" : containerStyle.padding }}>
        <div style={{ ...panelStyle, textAlign: "center", padding: isMobile ? "28px 20px" : "40px 36px" }}>
          <div style={eyebrow}>TOEFL iBT • Listening</div>
          <h2 style={{ color: C.greenText, margin: "0 0 10px", fontSize: isMobile ? "1.35rem" : "1.7rem" }}>
            Module 1 complete
          </h2>
          <p style={{ color: C.muted, lineHeight: 1.7, margin: "0 0 24px" }}>
            Module 2 has been selected based on your Module 1 answers. It has{" "}
            {countQuestions(listeningModules[path])} questions and is timed separately (
            {Math.round(MODULE_SECONDS.module2 / 60)} minutes).
          </p>
          <button type="button" onClick={startModule2} style={{ ...navBtnStyle, padding: "12px 32px" }}>
            Start Module 2 →
          </button>
        </div>
      </div>
    );
  }

  const moduleNumber = stage === "module1" ? 1 : 2;
  const answeredBefore = items.slice(0, itemIndex).reduce((n, it) => n + it.questions.length, 0);
  const moduleTotal = countQuestions(items);
  const showQuestions = audio === "done";

  return (
    <div style={{ ...containerStyle, padding: isMobile ? "90px 14px 16px" : containerStyle.padding }}>
      <div style={{ ...panelStyle, padding: isMobile ? "16px 14px 20px" : panelStyle.padding }}>
        {/* HEADER */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "8px", marginBottom: "8px" }}>
          <h1 style={{ color: C.green, fontSize: isMobile ? "1.15rem" : "1.6rem", margin: 0 }}>
            TOEFL Listening{" "}
            <span style={{ fontSize: "0.6em", color: C.muted, fontWeight: 600 }}>Module {moduleNumber} of 2</span>
          </h1>
          <TimerBadge timeLeft={timeLeft} isMobile={isMobile} />
        </div>

        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.6rem", fontSize: isMobile ? "0.85rem" : "0.95rem", flexWrap: "wrap", gap: "4px" }}>
          <span>
            Question {answeredBefore + 1}
            {item.questions.length > 1 && `–${answeredBefore + item.questions.length}`} of {moduleTotal}
          </span>
          <span style={{ color: C.accent, fontWeight: 600 }}>{TASK_LABELS[item.type]}</span>
        </div>

        <div style={progressContainerStyle}>
          <div style={{ ...progressBarStyle, width: `${((answeredBefore + item.questions.length) / moduleTotal) * 100}%` }} />
        </div>

        <div style={{ ...cardStyle, padding: isMobile ? "0.9rem 1rem" : cardStyle.padding }}>
          {/* AUDIO */}
          <div style={{ borderBottom: `1px solid ${C.border}`, paddingBottom: "0.8rem", marginBottom: "0.9rem" }}>
            <p style={{ margin: "0 0 10px", color: "#333", fontSize: isMobile ? "0.88rem" : "0.95rem" }}>
              {TASK_INSTRUCTIONS[item.type]}
              {item.title && <strong style={{ color: C.greenText }}> ({item.title})</strong>}
            </p>
            <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
              <button
                type="button"
                onClick={play}
                disabled={audio !== "ready"}
                style={{
                  ...audioBtnStyle,
                  background: audio === "playing" ? "#ff4757" : audio === "done" ? "#9ab9aa" : C.green,
                  cursor: audio === "ready" ? "pointer" : "default",
                  fontSize: isMobile ? "0.82rem" : "0.9rem",
                }}
              >
                {audio === "playing" ? "🔊 Playing..." : audio === "done" ? "✓ Audio played" : "▶ Play Audio"}
              </button>
              <span style={{ fontSize: "0.8rem", color: C.muted }}>
                {ttsAvailable
                  ? "The recording plays once. Questions appear when it ends."
                  : "Audio is not supported in this browser — the transcript will be shown instead."}
              </span>
            </div>
            {showTranscript && (
              <div style={{ marginTop: "12px", background: "#fff", border: `1px dashed ${C.border}`, borderRadius: "10px", padding: "10px 14px", fontSize: "0.9rem", lineHeight: 1.7, whiteSpace: "pre-wrap" }}>
                {item.lines.map((l) => `${l.speaker}: ${l.text}`).join("\n")}
              </div>
            )}
          </div>

          {/* QUESTIONS */}
          {!showQuestions ? (
            <p style={{ color: C.muted, fontSize: "0.9rem", margin: "0.4rem 0", textAlign: "center" }}>
              {audio === "playing" ? "Listen carefully..." : "Press Play when you are ready."}
            </p>
          ) : (
            item.questions.map((q, qi) => {
              const selected = answers[answerKey(item.id, qi)];
              return (
                <div key={qi} style={{ marginBottom: qi === item.questions.length - 1 ? 0 : "1.2rem" }}>
                  <p style={{ fontWeight: 600, margin: "0 0 8px", fontSize: isMobile ? "0.92rem" : "1rem" }}>
                    {answeredBefore + qi + 1}. {q.question}
                  </p>
                  <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: "8px" }}>
                    {q.options.map((opt, oi) => {
                      const isSel = selected === oi;
                      return (
                        <button
                          key={oi}
                          type="button"
                          onClick={() => setAnswers((prev) => ({ ...prev, [answerKey(item.id, qi)]: oi }))}
                          style={{
                            ...optionBtnStyle,
                            fontSize: isMobile ? "0.84rem" : optionBtnStyle.fontSize,
                            borderColor: isSel ? C.accent : "#dceee5",
                            background: isSel ? "#e8f5ee" : "#ffffff",
                          }}
                        >
                          <span style={radioCircleStyle}>{isSel && <span style={radioInnerStyle} />}</span>
                          <span>{opt}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* NAVIGATION */}
        <div style={{ display: "flex", justifyContent: "center", marginTop: isMobile ? "1.4rem" : "2rem", gap: isMobile ? "1rem" : "2rem", flexWrap: "wrap" }}>
          <button type="button" onClick={handleExit} style={exitBtnStyle}>
            Exit Test
          </button>
          <button
            type="button"
            onClick={handleNext}
            disabled={!showQuestions || !allAnswered}
            style={{ ...navBtnStyle, opacity: !showQuestions || !allAnswered ? 0.5 : 1, cursor: !showQuestions || !allAnswered ? "not-allowed" : "pointer" }}
          >
            {!isLastItem ? "Next →" : stage === "module1" ? "Next Module →" : "Finish Test ✓"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Styles ──────────────────────────────────────────────────────
const containerStyle = { maxWidth: "860px", margin: "0 auto", padding: "130px 24px 40px", color: "#111111" };

const panelStyle = {
  background: "#ffffff",
  border: `1px solid ${C.border}`,
  borderRadius: "20px",
  boxShadow: "0 6px 22px rgba(12, 87, 46, 0.08)",
  padding: "28px 32px 32px",
};

const eyebrow = {
  display: "inline-block",
  fontSize: "0.72rem",
  fontWeight: 700,
  letterSpacing: "1.6px",
  textTransform: "uppercase",
  color: C.green,
  border: `1px solid ${C.border}`,
  borderRadius: "50px",
  padding: "5px 14px",
  marginBottom: "14px",
};

const progressContainerStyle = { width: "100%", height: "4px", background: "#e0f0e8", borderRadius: "2px", marginBottom: "12px" };
const progressBarStyle = { height: "100%", background: C.accent, transition: "width 0.3s ease" };

const cardStyle = {
  background: C.softer,
  border: `1px solid ${C.border}`,
  borderRadius: "16px",
  padding: "1.1rem 1.4rem",
  boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
};

const audioBtnStyle = { padding: "8px 22px", border: "none", borderRadius: "22px", color: "#fff", fontWeight: "bold" };

const optionBtnStyle = {
  border: "1.5px solid",
  padding: "10px 12px",
  borderRadius: "9px",
  color: "#111111",
  cursor: "pointer",
  textAlign: "left",
  display: "flex",
  alignItems: "center",
  gap: "10px",
  fontSize: "0.9rem",
};

const radioCircleStyle = {
  width: "17px",
  height: "17px",
  borderRadius: "50%",
  border: `2px solid ${C.accent}`,
  flexShrink: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const radioInnerStyle = { width: "9px", height: "9px", borderRadius: "50%", background: C.accent };

const navBtnStyle = {
  padding: "9px 28px",
  background: C.greenDark,
  color: "#fff",
  border: "none",
  borderRadius: "50px",
  fontWeight: "bold",
  fontSize: "0.92rem",
};

const exitBtnStyle = {
  padding: "9px 20px",
  background: "transparent",
  border: `1px solid ${C.border}`,
  color: "#555",
  borderRadius: "50px",
  cursor: "pointer",
  fontSize: "0.88rem",
};

export default ToeflListening;
